import { Component, inject, NgZone, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import type { TwoButtonFormDialogData } from '@pixomaticc/ngx-ui';
import { JwtInterface } from '@uzenith360/jwt-utils';
import { AuthManagerService } from '@uzenith360/ngx-jwt-auth';
import { fadeAnimation, NavigateEventService, NavigationAnimationComponent } from '@uzenith360/ngx-navigation-animation';
import OnlineNotification from '@uzenith360/offline-notification';
import { Subscription, Subject, firstValueFrom } from 'rxjs';
import { PinUpdateService } from './home/settings/pin-update/pin-update.service';
import { HttpError, httpStatus } from '@uzenith360/http-utils';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { User } from '@pixomaticc/ngx-ui';
import { VersionCheckService } from './version-check.service';
import { WhatsnewDialogComponent } from './whatsnew-dialog.component';
import { ReferralIntroDialogComponent } from './home/settings/referral-intro-dialog.component';
import { ReferralEarningsService } from './home/settings/referral-earnings/referral-earnings.service';
import { PwaInstallService } from './pwa-install.service';
import { OnboardingStateService } from './onboarding/onboarding-state.service';
import { environment } from 'src/environments/environment';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  animations: [fadeAnimation],
  providers: [NavigateEventService],
  imports: [NavigationAnimationComponent, RouterOutlet, MatIcon],
  standalone: true
})
export class AppComponent implements OnInit {
  public title = 'Creatives | OurPixo';
  public isPageLoading: boolean = false;
  public page: string = document.location.pathname;
  public showUpdatePill: boolean = false;

  private whatsnewChecked = false;
  private referralIntroShown = false;

  // Change this path to control which page triggers the referral intro dialog.
  // '/home'          → fires on the very first page after login (any /home route)
  // '/home/settings' → fires only when the user navigates to Settings
  private static readonly REFERRAL_INTRO_TRIGGER_PATH = '/home';

  // Don't ask brand-new users to refer anyone — wait until they've had a few
  // days to experience the product themselves.
  private static readonly REFERRAL_INTRO_MIN_ACCOUNT_AGE_DAYS = 3;
  // public isProductsPage!: boolean;

  public static hasPinStoreId: string = 'ys6HSl';
  public static onAuthGuardLoginUserSubject: Subject<void> = new Subject();

  private onlineNotification!: OnlineNotification;
  private installPromptShown = false;
  private navigateEventServiceSubscription!: Subscription;
  private navigateEventServiceStartSubscription!: Subscription;
  private navigateEventServiceStopSubscription!: Subscription;
  private onAuthGuardLoginUserSubscription!: Subscription;
  private routerEventsSubscription!: Subscription;

  private router = inject(Router);

  constructor(
    private readonly navigateEventService: NavigateEventService,
    private readonly zone: NgZone,
    private readonly dialog: MatDialog,
    private readonly pinUpdateService: PinUpdateService,
    private readonly authManagerService: AuthManagerService,
    private readonly versionCheckService: VersionCheckService,
    private readonly referralEarningsService: ReferralEarningsService,
    private readonly matSnackBar: MatSnackBar,
    private readonly pwaInstallService: PwaInstallService,
    private readonly onboardingStateService: OnboardingStateService,
  ) {
    this.navigateEventServiceSubscription = this.navigateEventService.navigation$.subscribe(
      (navigation) => {
        this.page = /*JSON.parse(navigation).url*/document.location.pathname;
        // this.isProductsPage = JSON.parse(navigation).url.startsWith('/products');
      }
    );

    this.navigateEventServiceStartSubscription = this.navigateEventService
      .navigationStart$
      .subscribe(
        (navigation) => {
          this.isPageLoading = true;
        }
      );

    this.navigateEventServiceStopSubscription = this.navigateEventService
      .navigationStop$
      .subscribe(
        (navigation) => {
          setTimeout(() => {
            this.isPageLoading = false;
          }, 0);
        }
      );

    this.routerEventsSubscription = this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        sessionStorage.removeItem('chunkReloadAttempted');
        this.maybeShowInstallPrompt();

        if (!e.url.startsWith('/auth')) {
          // First non-auth navigation of the session decides between the welcome
          // experience (brand-new users) and What's New → referral intro (everyone else).
          if (!this.whatsnewChecked) {
            this.whatsnewChecked = true;
            this._handleFirstNavigation(e.url);
          } else {
            // Subsequent navigations — handles the case where REFERRAL_INTRO_TRIGGER_PATH
            // is a specific sub-route the user didn't land on initially.
            this._maybeShowReferralIntro(e.url);
          }
        }
      }
    });

    // only load tawk on desktop screen size, and never in a build whose environment switches it off. Only
    // environment.nvtech-staging.ts carries `tawkChat` (false: the N.V Tech test build, OWNER-RULINGS f8db3ef); the
    // other three environments lack the key, so the `in` test is false there and Tawk loads exactly as before.
    const tawkChatOff = 'tawkChat' in environment && environment.tawkChat === false;

    if (window.screen.width > 800 && !tawkChatOff) {
      this.initTawk();
    }
  }
  ngOnInit(): void {
    // setTimeout(() => {
    // if (this.swUpdate.isEnabled) {
    //   this.swUpdate.versionUpdates.subscribe((value: VersionEvent) => {
    //     if (value.type === 'VERSION_READY' && confirm("New version available. Load New Version?")) {
    //       window.location.reload();
    //     }
    //   });
    // }
    // }, 5000);

    this.zone.runOutsideAngular(
      () => this.onlineNotification = OnlineNotification.getInstance(window),
    );

    this.onAuthGuardLoginUserSubscription
      = AppComponent.onAuthGuardLoginUserSubject.subscribe(() => this.setPIN());

    this.versionCheckService.start(() => {
      this.zone.run(() => { this.showUpdatePill = true; });
    });
  }

  public getRouterOutletState(outlet: { isActivated: any; activatedRoute: any; }): any {
    return outlet.isActivated ? outlet.activatedRoute : '';
  }

  public refreshPage(): void {
    window.location.reload();
  }

  public dismissUpdatePill(): void {
    this.showUpdatePill = false;
  }

  private async _handleFirstNavigation(url: string): Promise<void> {
    let user: User | undefined;

    try {
      // forceAuth=false — MUST NOT trigger the login popup here: this runs on
      // unguarded routes too (/error, 404), where the user may be logged out.
      user = await this.authManagerService.getLoggedInUser(false) as unknown as User;
    } catch { /* not logged in — fall through to the regular flow */ }

    const data = await this.versionCheckService.checkForWhatsnew();

    // Brand-new user (account < 1 day, never subscribed, nothing uploaded):
    // everything is new to them, so the changelog is meaningless — mark it seen
    // silently and show the welcome experience instead of any other dialog.
    if (!!user && url.startsWith('/home') && this.onboardingStateService.shouldShowWelcome(user)) {
      try {
        const { WelcomeExperienceComponent } = await import('./onboarding/welcome-experience.component');

        if (data) {
          this.versionCheckService.markWhatsnewSeen(data.version);
        }

        // Mark seen on open (not close) so a mid-animation refresh doesn't replay it.
        this.onboardingStateService.markWelcomeSeen(user);

        this.dialog.open(WelcomeExperienceComponent, {
          data: { firstName: user.firstName },
          width: '100vw',
          maxWidth: '100vw',
          height: '100dvh',
          panelClass: 'welcome-experience-panel',
          backdropClass: 'welcome-experience-backdrop',
          autoFocus: false,
        });
      } catch { /* chunk failed to load — nothing marked seen, retries next session */ }

      return;
    }

    if (data) {
      this.versionCheckService.markWhatsnewSeen(data.version);
      this.dialog.open(WhatsnewDialogComponent, {
        data,
        width: '480px',
        maxWidth: '95vw',
        panelClass: 'whatsnew-dialog-panel',
      }).afterClosed().subscribe(() => this._maybeShowReferralIntro(url));
    } else {
      this._maybeShowReferralIntro(url);
    }
  }

  private _maybeShowReferralIntro(url: string): void {
    if (this.referralIntroShown) { return; }
    if (!url.startsWith(AppComponent.REFERRAL_INTRO_TRIGGER_PATH)) { return; }

    // Claim synchronously so rapid navigations don't spawn concurrent auth lookups.
    this.referralIntroShown = true;

    this.authManagerService.getLoggedInUser().then((_user: unknown) => {
      // The referral ask lands better once the user has experienced the product —
      // hold it back for the first days of a new account.
      if (this.onboardingStateService.accountAgeDays(_user as User) < AppComponent.REFERRAL_INTRO_MIN_ACCOUNT_AGE_DAYS) { return; }

      const referralCode = (_user as User)?.referralCode;
      if (!referralCode) {
        // No referral code yet — let it retry on the next navigation.
        this.referralIntroShown = false;
        return;
      }

      const permanentKey = `pixo_referral_intro_${referralCode}`;
      const sessionKey   = `pixo_referral_intro_session_${referralCode}`;
      if (localStorage.getItem(permanentKey) || sessionStorage.getItem(sessionKey)) { return; }

      sessionStorage.setItem(sessionKey, 'true');

      this.referralEarningsService.getEarningsPotential().then(earningsPotential => {
        this.dialog.open(ReferralIntroDialogComponent, {
          data: { referralCode, earningsPotential },
          width: '460px',
          maxWidth: '95vw',
          panelClass: 'referral-intro-dialog-panel',
        });
      });
    }).catch(() => { this.referralIntroShown = false; });
  }

  private maybeShowInstallPrompt(): void {
    if (this.installPromptShown) { return; }
    if (!this.pwaInstallService.canInstall()) { return; }
    if (localStorage.getItem('pwaInstallDismissed')) { return; }
    // Don't compete with an open dialog (e.g. the welcome experience) — the
    // prompt retries on the next navigation since no flag has been set yet.
    if (this.dialog.openDialogs.length) { return; }

    this.installPromptShown = true;
    localStorage.setItem('pwaInstallDismissed', '1');

    this.matSnackBar
      .open('Add OurPixo to your home screen', 'Install', { duration: 12000 })
      .onAction()
      .subscribe(() => this.pwaInstallService.install());
  }

  private initTawk(): void {
    this.zone.runOutsideAngular(
      () => {
        // Tawk reads the GLOBAL Tawk_API — assign to window, not a local var.
        // onLoad forces the layout pass a manual window-resize would trigger:
        // tawk's pre-created chat iframe (widget config has maximized 0×0) can
        // otherwise flash as an unsized dark band at the bottom while booting.
        const tawkApi = (window as any)['Tawk_API'] || {};

        tawkApi.onLoad = () => window.dispatchEvent(new Event('resize'));

        (window as any)['Tawk_API'] = tawkApi;
        (window as any)['Tawk_LoadStart'] = new Date();

        (function () {
          var s1 = document.createElement("script"), s0 = document.getElementsByTagName("script")[0];
          s1.async = true;
          s1.src = 'https://embed.tawk.to/63ff94a94247f20fefe369bc/default';
          s1.charset = 'UTF-8';
          s1.setAttribute('crossorigin', '*');
          s0?.parentNode?.insertBefore(s1, s0);
        })();
      }
    )
  }

  ngOnDestroy(): void {
    this.versionCheckService.stop();

    this.navigateEventServiceSubscription?.unsubscribe();

    this.navigateEventServiceStartSubscription?.unsubscribe();

    this.navigateEventServiceStopSubscription?.unsubscribe();

    this.onlineNotification?.destroy();

    this.onAuthGuardLoginUserSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
  }

  private async pinDialog(
    pinValue: string,
    confirmPinValue: string,
    body: string = 'Set a **6 Digit** login PIN for quick, convienent access to your account',
    isError?: boolean,
  ): Promise<{ [field: string]: string } | null> {
    const { TwoButtonFormDialogComponent } = await import('@pixomaticc/ngx-ui');
    return firstValueFrom(
      this.dialog.open(
        TwoButtonFormDialogComponent,
        {
          panelClass: 'dialog-panel',
          backdropClass: 'ngx-ui-dialog-backdrop',
          data: {
            heading: 'Login PIN',
            body,
            isError,
            cancelText: 'Don\'t show again',
            okayText: 'Save',
            form: [
              { name: 'pin', placeHolder: 'Enter **6 Digit** PIN', type: 'password', required: true, regexValidator: '[0-9]{6}', regexErrorMessage: 'must be exactly **6 Digits**', autocomplete: 'new-password', default: pinValue },
              { name: 'confirm_pin', placeHolder: 'Confirm **6 Digit** PIN', type: 'password', required: true, regexValidator: '[0-9]{6}', regexErrorMessage: 'must be exactly **6 Digits**', autocomplete: 'new-password', default: confirmPinValue }
            ]
          } as TwoButtonFormDialogData
        },
      ).afterClosed()
    );
  }

  private async setPIN() {
    let pinValue: string = '';
    let confirmPinValue: string = '';
    let isError: boolean | undefined;
    let body: string | undefined;

    let formData;

    do {
      formData = await this.pinDialog(pinValue, confirmPinValue, body, isError);

      if (!!formData) {
        const pin: string = String(formData['pin'] ?? '').trim();
        const confirmPin: string = String(formData['confirm_pin'] ?? '').trim();

        if (pin === confirmPin && /[0-9]{6}/.test(pin)) {
          // save data to database
          try {
            const { data, message, statusCode }
              = (await firstValueFrom(this.pinUpdateService.updatePin(pin)));

            if (statusCode !== httpStatus.OK) {
              throw new HttpError(message, statusCode);
            }

            const jwt: JwtInterface | undefined = data;

            jwt && this.authManagerService.setJWT(jwt);

            formData = undefined;
          } catch (e: unknown) {
            console.error(e);

            isError = true;
            pinValue = pin;
            confirmPinValue = confirmPin;
            body = e instanceof HttpError ? e.message : String(e);
          }
        } else {
          isError = true;
          pinValue = pin;
          confirmPinValue = confirmPin;
          body = 'The PIN and confirm PIN must be the same and exactly 6 digits';
        }
      } else {
        localStorage.setItem(AppComponent.hasPinStoreId, '1');
      }
    } while (!!formData);
  }
}
